import React, { useState, useEffect } from "react";
import { useGoogleSyncStore } from "../stores/googleSyncStore";
import type {
  ApiCalendar,
  ApiCalendarEntry,
  CreateEventInput,
  UpdateEventInput,
  ApiDate,
} from "../lib/types";
import { ColorPicker } from "./ColorPicker";
import { DateTimeField, type DateTimeValue } from "./DateTimeField";
import { requestNotificationPermission } from "../lib/reminderScheduler";
import {
  buildRruleString,
  parsePickerStateFromRrule,
  type Frequency,
} from "../lib/rrule";

interface EventFormProps {
  calendars: ApiCalendar[];
  event?: ApiCalendarEntry | null;
  defaultDate?: ApiDate;
  defaultCalendarId?: string;
  onSubmit: (calendarId: string, data: CreateEventInput | UpdateEventInput) => void;
  onCancel: () => void;
  loading?: boolean;
}

export function EventForm({
  calendars,
  event,
  defaultDate,
  defaultCalendarId,
  onSubmit,
  onCancel,
  loading,
}: EventFormProps) {
  const isSyncedCalendar = useGoogleSyncStore((s) => s.isSyncedCalendar);
  const editableCalendars = calendars.filter((c) => !isSyncedCalendar(c.id));

  const now = new Date();
  const todayDate: ApiDate = defaultDate ?? {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };

  const [heading, setHeading] = useState(event?.heading ?? "");
  const [content, setContent] = useState(event?.content ?? "");
  const [when, setWhen] = useState<DateTimeValue>(() => ({
    start: event?.date.start ?? todayDate,
    end: event?.date.end ?? todayDate,
    time: event
      ? event.time
        ? { start: event.time.start, end: event.time.end }
        : null
      : {
          start: { hour: now.getHours(), minute: 0 },
          end: { hour: (now.getHours() + 1) % 24, minute: 0 },
        },
  }));
  const hasTime = when.time !== null;
  const [calendarId, setCalendarId] = useState(
    event?.calendarId ?? defaultCalendarId ?? editableCalendars[0]?.id ?? "",
  );
  const [color, setColor] = useState(event?.color ?? "");
  const [reminderMinutes, setReminderMinutes] = useState<number | null>(
    event?.reminderMinutesBefore ?? null,
  );
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>(
      typeof Notification !== "undefined" ? Notification.permission : "denied",
    );
  const initialPicker = parsePickerStateFromRrule(event?.rrule ?? null);
  const [recurFreq, setRecurFreq] = useState<Frequency | null>(initialPicker.freq);
  const [recurUntil, setRecurUntil] = useState<string>(initialPicker.until);

  useEffect(() => {
    if (!event && defaultCalendarId) setCalendarId(defaultCalendarId);
  }, [defaultCalendarId, event]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: CreateEventInput = {
      heading: heading.trim(),
      content: content.trim() || undefined,
      color: color || undefined,
      date: { start: when.start, end: when.end },
      ...(when.time ? { time: when.time } : {}),
      reminderMinutesBefore: reminderMinutes,
      rrule: buildRruleString({ freq: recurFreq, until: recurUntil }),
    };
    onSubmit(calendarId, data);
  };

  // For all-day events the "minutes before midnight of the start day"
  // semantics get confusing — limit the choices to day-granular offsets.
  const timedReminderOptions: { value: number | null; label: string }[] = [
    { value: null, label: "None" },
    { value: 0, label: "At time of event" },
    { value: 5, label: "5 minutes before" },
    { value: 15, label: "15 minutes before" },
    { value: 30, label: "30 minutes before" },
    { value: 60, label: "1 hour before" },
    { value: 24 * 60, label: "1 day before" },
  ];
  const allDayReminderOptions: { value: number | null; label: string }[] = [
    { value: null, label: "None" },
    { value: 0, label: "On the day (midnight)" },
    { value: 24 * 60, label: "1 day before" },
    { value: 7 * 24 * 60, label: "1 week before" },
  ];
  const reminderOptions = hasTime ? timedReminderOptions : allDayReminderOptions;

  // If the previously-chosen reminder isn't valid for the current
  // all-day toggle, drop back to None to avoid surprising the user.
  // Depending on hasTime keeps this effect stable across renders.
  useEffect(() => {
    if (reminderMinutes == null) return;
    const allowed = (hasTime ? timedReminderOptions : allDayReminderOptions).some(
      (o) => o.value === reminderMinutes,
    );
    if (!allowed) setReminderMinutes(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTime, reminderMinutes]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="label" htmlFor="evt-heading">Title</label>
        <input
          id="evt-heading"
          className="input"
          value={heading}
          onChange={(e) => setHeading(e.target.value)}
          required
          autoFocus
        />
      </div>

      <div className="form-group">
        <label className="label" htmlFor="evt-calendar">Calendar</label>
        <select
          id="evt-calendar"
          className="input"
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
        >
          {editableCalendars.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <DateTimeField value={when} onChange={setWhen} />
      </div>

      <div className="form-group">
        <label className="label" htmlFor="evt-content">Notes</label>
        <textarea
          id="evt-content"
          className="input"
          rows={3}
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="label" htmlFor="evt-recur">Repeats</label>
          <select
            id="evt-recur"
            className="input"
            value={recurFreq ?? ""}
            onChange={(e) =>
              setRecurFreq(e.target.value === "" ? null : (e.target.value as Frequency))
            }
          >
            <option value="">Never</option>
            <option value="DAILY">Daily</option>
            <option value="WEEKLY">Weekly</option>
            <option value="MONTHLY">Monthly</option>
            <option value="YEARLY">Yearly</option>
          </select>
        </div>
        {recurFreq && (
          <div className="form-group" style={{ flex: 1 }}>
            <label className="label" htmlFor="evt-recur-until">Until (optional)</label>
            <input
              id="evt-recur-until"
              className="input"
              type="date"
              value={recurUntil}
              onChange={(e) => setRecurUntil(e.target.value)}
            />
          </div>
        )}
      </div>
      {recurFreq && event && (
        <p className="muted" style={{ fontSize: "0.8rem", marginTop: -8, marginBottom: 12 }}>
          Editing or deleting this event affects the entire series.
        </p>
      )}

      <div className="form-group">
        <label className="label" htmlFor="evt-reminder">Reminder</label>
        <select
          id="evt-reminder"
          className="input"
          value={reminderMinutes ?? ""}
          onChange={async (e) => {
            const next = e.target.value === "" ? null : Number(e.target.value);
            setReminderMinutes(next);
            if (next != null && notificationPermission === "default") {
              const granted = await requestNotificationPermission();
              setNotificationPermission(granted);
            }
          }}
        >
          {reminderOptions.map((opt) => (
            <option key={opt.label} value={opt.value ?? ""}>
              {opt.label}
            </option>
          ))}
        </select>
        {reminderMinutes != null && notificationPermission === "denied" && (
          <p className="muted" style={{ fontSize: "0.8rem", marginTop: 4 }}>
            Notifications are blocked — the reminder will be saved, but no
            popup will appear. Allow notifications in your browser settings
            to enable popups.
          </p>
        )}
      </div>

      <div className="form-group">
        <label className="label">Color (optional)</label>
        <ColorPicker value={color} onChange={setColor} />
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? "Saving..." : event ? "Update" : "Create"}
        </button>
      </div>
    </form>
  );
}
